import { useNavigate, useParams } from 'react-router';
import * as React from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import { heroBackground } from 'src/components/LotteryView';
import config from '../../../config';
import { InputAdornment, MenuItem, FormControl, InputLabel, Select, SelectChangeEvent, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Stack from '@mui/material/Stack';
import PrivacyPage from './PrivacyPage';
import { useAuth } from 'src/util/authenticationProvider';
import { useState } from 'react';
import { standaloneTheme } from 'src/components/LotteryView/theme';


export default function JoinLotteryPage() {

  const { id } = useParams();
  const [logoUrl, setLogoUrl] = useState<string>('');
  const navigate = useNavigate();
  const { tokens } = useAuth();

  const [locations, setLocations] = React.useState([]);
  const [selectedLocation, setSelectedLocation] = React.useState('');
  const [privacyAccepted, setPrivacyAccepted] = React.useState(false);
  const [privacyOpen, setPrivacyOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(config.apiUrl + '/api/public/location/all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(id)
      });
      const logoRes = await fetch(config.apiUrl + '/api/public/media/logo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(id)
      });
      if (logoRes.ok) {
        const logoData = await logoRes.json();
        const logoEntry = logoData.content || logoData;
        if (logoEntry?.image) {
          setLogoUrl(logoEntry.image);
        }
      }

        if (response.ok) {
          const data = await response.json();
          setLocations(data.content || []);
        }
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchLocations();
  }, [tokens?.accessToken, id]);

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    setSelectedLocation(event.target.value as string);
  };

  const handlePrivacyOpen = async (e: React.MouseEvent) => {
    e.preventDefault();
    setPrivacyOpen(true);
  };

  const handlePrivacyClose = () => {
    setPrivacyOpen(false);
  };

  const handlePrivacyAccept = () => {
    setPrivacyAccepted(true);
    setPrivacyOpen(false);
  };

  const handlePrivacyCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrivacyAccepted(event.target.checked);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!privacyAccepted) {
      setPrivacyOpen(true);
      return;
    }
    const formData = new FormData(event.currentTarget);
    const requestData = {
      email: formData.get('email'),
      phone: '+49' + formData.get('phone').toString().replace(/^0+/, ''),
      name: formData.get('name'),
      locationId: selectedLocation,
      privacyAccepted: privacyAccepted
    };
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*'
      },
      body: JSON.stringify(requestData)
    };
    try {
      const response = await fetch(config.apiUrl + `/api/lottery/join/${id}`, requestOptions);
      if (!response.ok) {
        throw new Error('Teilnahme fehlgeschlagen');
      }
      const responseData = await response.json();
      const luckynumber = responseData?.content;
      navigate("/lucky-number", { state: { lotteryId: id, luckyNumber: luckynumber } });
    } catch (err) {
      console.log("Couldn't fetch current Game", err);
    }
  }

  return (
    <ThemeProvider theme={standaloneTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          ...heroBackground('channel'),
          '&:after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 20% 20%, rgba(243,189,71,0.16), transparent 35%), radial-gradient(circle at 80% 10%, rgba(84,214,255,0.14), transparent 30%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack spacing={1.5} textAlign="center" alignItems="center" mb={3}>
            {logoUrl && <img src={logoUrl} alt="Logo" style={{ maxWidth: 180, filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.35))' }} />}
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: 1 }}>
              Mach mit &amp; gewinne
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
              Sichere dir dein Ticket in weniger als einer Minute.
            </Typography>
          </Stack>
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'linear-gradient(135deg, rgba(12,14,24,0.92), rgba(18,19,36,0.9))',
              boxShadow: '0 30px 70px rgba(0,0,0,0.55)',
              overflow: 'hidden',
              position: 'relative',
              backdropFilter: 'blur(10px)',
              '&:before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(145deg, rgba(255,202,126,0.12), rgba(120,196,255,0.08))',
                pointerEvents: 'none'
              }
            }}
          >
            <Typography component="h1" variant="h5" textAlign="center" gutterBottom sx={{ position: 'relative' }}>
              Jetzt teilnehmen
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, position: 'relative' }}>
              <Stack spacing={2}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="name"
                  label="Name"
                  name="name"
                  autoComplete="name"
                  autoFocus
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.04)'
                    }
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="E-Mail-Adresse"
                  name="email"
                  autoComplete="email"
                  inputProps={{
                    type: 'email'
                  }}
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.04)'
                    }
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="phone"
                  label="Telefonnummer"
                  id="phone"
                  autoComplete="phone"
                  sx={{
                    'input::-webkit-inner-spin-button': {
                      appearance: 'none',
                      margin: 0
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+49</InputAdornment>,
                    type: 'number',
                    sx: {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.04)'
                    }
                  }}
                />
                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="location-label">Standort</InputLabel>
                  <Select
                    labelId="location-label"
                    id="location"
                    value={selectedLocation}
                    label="Standort"
                    onChange={handleLocationChange}
                    name="location"
                    required
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.04)'
                    }}
                  >
                    {locations.map((loc: any) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  name="privacyAccepted"
                  id="privacyAccepted"
                  control={
                    <Checkbox
                      checked={privacyAccepted}
                      onChange={handlePrivacyCheckboxChange}
                      color="primary"
                      required
                    />
                  }
                  label={
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                      Ich akzeptiere die{' '}
                      <Box
                        component="a"
                        href="#"
                        onClick={handlePrivacyOpen}
                        sx={{ color: '#f3bd47', textDecoration: 'none', fontWeight: 600 }}
                      >
                        Datenschutzbestimmungen
                      </Box>
                    </Box>
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 1,
                    px: 5,
                    py: 2,
                    borderRadius: 4,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    background: 'linear-gradient(120deg, #fbb034, #ffdd00)',
                    color: '#2b1900',
                    animation: 'pulseButton 2s ease-in-out infinite',
                    '@keyframes pulseButton': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                      '100%': { transform: 'scale(1)' }
                    }
                  }}
                >
                  Teilnahme absenden
                </Button>
              </Stack>
            </Box>
          </Card>
          <Box
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.72)',
              mt: 2
            }}
          >
            <Typography variant="body2">
              Fragen? Schreib uns und wir helfen dir gerne weiter.
            </Typography>
          </Box>
          <Dialog
            open={privacyOpen}
            onClose={handlePrivacyClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                background: 'linear-gradient(135deg, rgba(12,14,24,0.95), rgba(18,19,36,0.92))',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 30px 70px rgba(0,0,0,0.55)',
                color: '#fff',
                backdropFilter: 'blur(10px)'
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, letterSpacing: 0.5 }}>Datenschutz</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.12)' }}>
              <PrivacyPage />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handlePrivacyClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Schließen
              </Button>
              <Button
                onClick={handlePrivacyAccept}
                variant="contained"
                sx={{
                  background: 'linear-gradient(120deg, #fbb034, #ffdd00)',
                  color: '#2b1900',
                  fontWeight: 700,
                  '&:hover': {
                    background: 'linear-gradient(120deg, #ffc85a, #ffe45f)'
                  }
                }}
              >
                Akzeptieren
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
